/**
 * Mobile customers view — Phase 2.2.b of the admin-frontend
 * decomposition.
 *
 * Owns the Mobile Customers screen (`#mobileCustomersView`):
 *   - Two tabs: Customers list + Pending payments.
 *   - Monthly-new-customers trend bar chart.
 *   - Customer detail dialog (profile / subscriptions / payments / auth
 *     keys + a "refresh billing" button that re-fetches Porta data).
 *   - Pending payments table with Complete/Mark-manual actions.
 *   - Mobile session revoke.
 *
 * What stays in admin.js (injected):
 *   - `escapeHtml`, `openDialog`.
 *   - The customer mobile-session refresh URL (legacy hardcoded
 *     `/api/admin/mobile/customers/{id}/refresh-billing` — could move to
 *     `api.routes` later, but kept inline for now).
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
 */

import { escapeHtml, debounce, formatDateTimeFull, formatCurrency, formatNumber } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

let dom = null;
let mounted = false;
let currentProfileId = null;
let activeTab = 'customers';
// Payments for the open customer detail, kept so the status filter re-renders
// without a refetch; and the payment currently in the manual-complete dialog.
let mcPaymentsCache = [];
let mcManualPayment = null;
let customersOffset = 0;
const customersLimit = 20;
let customersQuery = '';
let customersReqSeq = 0;
let pendingOffset = 0;
const pendingLimit = 20;
let pendingQuery = '';
let pendingReqSeq = 0;

const helpers = { escapeHtml, openDialog };

/**
 * Customer-facing balance math — a faithful port of the inet-go mobile app's
 * `computeDueForAccount` (mobile_apps/inet-go/lib/finance.ts). Support engineers
 * need to see the same numbers the customer sees, not the raw Porta balance.
 * Keep this in sync with finance.ts if that logic changes.
 *
 *   postpaid (ctl 1): amount due = ceil(overdue_balance) when > 0
 *   prepaid  (ctl 2): amount due = ceil(balance + periodic_bill) when > 0
 *   other:            amount due = ceil(periodic_bill - balance) when > 0
 */
function computeCustomerDue(acc, periodicBill) {
  const ctl = Number(acc?.porta_balance_control_type ?? 0);
  const p = Number(periodicBill ?? 0);
  if (ctl === 1) {
    const ob = Number(acc?.overdue_balance ?? 0);
    const due = Number.isFinite(ob) ? ob : 0;
    return { displayAmountDue: due > 0 ? Math.ceil(Math.abs(due)) : 0, hasAmountDue: due > 0 };
  }
  if (ctl === 2) {
    const raw = Number(acc?.balance ?? 0) + p;
    return { displayAmountDue: raw > 0 ? Math.ceil(Math.abs(raw)) : 0, hasAmountDue: raw > 0 };
  }
  const amountDue = Number(acc?.balance ?? 0) - p;
  return { displayAmountDue: amountDue < 0 ? Math.ceil(Math.abs(amountDue)) : 0, hasAmountDue: amountDue < 0 };
}

// Available funds (prepaid credit) the customer sees, mirroring dashboard.tsx:
// shown only for non-postpaid accounts whose balance is negative.
function customerAvailableFunds(acc) {
  const ctl = Number(acc?.porta_balance_control_type ?? 0);
  const bal = Number(acc?.balance ?? 0);
  return (ctl !== 1 && bal < 0) ? Math.abs(bal) : null;
}

// Builds the profile detail grid. Accepts either the detail endpoint's profile
// object or get_account's account snapshot (refresh-billing) — both carry the
// fields needed below. Shows the customer type and the customer-facing balance
// (available funds + amount due) alongside the raw Porta balance, so support
// sees the same figures the customer does in the mobile app.
function mobileProfileGridHtml(p) {
  const esc = escapeHtml;
  const fmtMoney = (n) => formatCurrency(n);
  const ctl = Number(p?.porta_balance_control_type ?? 0);
  const typeLabel = p?.customer_type
    ? (p.customer_type.charAt(0).toUpperCase() + p.customer_type.slice(1))
    : (ctl === 1 ? 'Postpaid' : ctl === 2 ? 'Prepaid' : '—');
  const due = computeCustomerDue(p, p?.periodic_bill);
  const avail = customerAvailableFunds(p);
  const availHtml = avail != null
    ? `<div class="field"><label>Available funds (customer)</label><div style="color:#2E7D32;font-weight:600;">${fmtMoney(avail)}</div></div>`
    : '';
  const dueHtml = `<div class="field"><label>Amount due (customer)</label><div style="${due.hasAmountDue ? 'color:#C62828;font-weight:600;' : ''}">${due.hasAmountDue ? fmtMoney(due.displayAmountDue) : '—'}</div></div>`;
  return `<div class="form-grid" style="grid-template-columns: repeat(4, minmax(0,1fr));">
    <div class="field"><label>Phone</label><div>${esc(p?.phone_number || '')}</div></div>
    <div class="field"><label>Name</label><div>${esc(p?.name || '')}</div></div>
    <div class="field"><label>Email</label><div>${esc(p?.email || '')}</div></div>
    <div class="field"><label>Customer type</label><div>${esc(typeLabel)}</div></div>
    ${availHtml}
    ${dueHtml}
    <div class="field"><label>Balance (Porta raw)</label><div id="mcBalanceVal">${fmtMoney(p?.balance)}</div></div>
    <div class="field"><label>Due Date</label><div id="mcDueVal">${p?.invoice_due_date ? formatDateTimeFull(p.invoice_due_date) : ''}</div></div>
  </div>`;
}

  function resolveDom() {
    if (dom) return dom;
    dom = {
      tabBar: document.getElementById('mobileCustomersTabBar'),
      tabCustomers: document.getElementById('mobileCustomersTabCustomers'),
      tabPending: document.getElementById('mobileCustomersTabPending'),
      tbody: document.getElementById('mobileCustomersTbody'),
      search: document.getElementById('mobileCustomerSearch'),
      prev: document.getElementById('mobileCustomersPrev'),
      next: document.getElementById('mobileCustomersNext'),
      pageInfo: document.getElementById('mobileCustomersPageInfo'),
      total: document.getElementById('mobileCustomersTotal'),
      sessions: document.getElementById('mobileCustomersSessions'),
      trend: document.getElementById('mobileCustomersTrend'),
      dlg: document.getElementById('mobileCustomerDlg'),
      profile: document.getElementById('mcProfile'),
      subs: document.getElementById('mcSubs'),
      pays: document.getElementById('mcPays'),
      payStatusFilter: document.getElementById('mcPayStatusFilter'),
      keys: document.getElementById('mcKeys'),
      conns: document.getElementById('mcConns'),
      refreshBtn: document.getElementById('mcRefreshBtn'),
      manualPayDlg: document.getElementById('mcManualPayDlg'),
      manualPayForm: document.getElementById('mcManualPayForm'),
      manualPaySummary: document.getElementById('mcManualPaySummary'),
      manualPayerName: document.getElementById('mcManualPayerName'),
      manualProof: document.getElementById('mcManualProof'),
      manualPaySubmit: document.getElementById('mcManualPaySubmit'),
      pendingTbody: document.getElementById('mobilePendingPaymentsTbody'),
      pendingSearch: document.getElementById('mobilePendingPaymentSearch'),
      pendingRefresh: document.getElementById('mobilePendingPaymentsRefresh'),
      pendingPurgeStale: document.getElementById('mobilePendingPurgeStaleBtn'),
      pendingPrev: document.getElementById('mobilePendingPaymentsPrev'),
      pendingNext: document.getElementById('mobilePendingPaymentsNext'),
      pendingPageInfo: document.getElementById('mobilePendingPaymentsPageInfo'),
    };
    return dom;
  }

  function renderTrend(series = []) {
    const d = resolveDom();
    if (!d.trend) return;
    d.trend.innerHTML = '';
    if (!Array.isArray(series) || !series.length) {
      d.trend.innerHTML = '<div style="color:var(--sub); font-size:12px;">No data</div>';
      return;
    }
    const maxCount = series.reduce((acc, item) => Math.max(acc, Number(item.count || 0)), 0) || 1;
    series.forEach((item) => {
      const count = Number(item.count || 0);
      const bar = document.createElement('div');
      bar.style.flex = '1';
      bar.style.minWidth = '6px';
      bar.style.height = `${Math.max(4, Math.round((count / maxCount) * 100))}%`;
      bar.style.background = 'var(--pri)';
      bar.style.borderRadius = '4px';
      bar.title = `${item.month}: ${count}`;
      d.trend.appendChild(bar);
    });
  }

  function renderCustomers(items = [], summary = {}) {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    if (!d.tbody) { console.warn('mobile-customers: #mobileCustomersTbody missing.'); return; }
    d.tbody.innerHTML = '';
    if (d.total) d.total.textContent = summary.total != null ? formatNumber(summary.total) : '—';
    if (d.sessions) d.sessions.textContent = summary.total_sessions != null ? formatNumber(summary.total_sessions) : '—';
    renderTrend(summary.monthly_new || []);
    if (!items.length) {
      d.tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No customers</td></tr>';
      if (d.pageInfo) d.pageInfo.textContent = `${Math.floor(customersOffset / customersLimit) + 1}`;
      return;
    }
    items.forEach((c, idx) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${idx + 1 + customersOffset}</td>
        <td>${escape(c.phone_number || '')}</td>
        <td>${escape(c.name || '')}</td>
        <td>${formatCurrency(c.balance)}</td>
        <td>${c.invoice_due_date ? formatDateTimeFull(c.invoice_due_date) : ''}</td>
        <td>${c.last_login_at ? formatDateTimeFull(c.last_login_at) : '—'}</td>
        <td>${c.created_at ? formatDateTimeFull(c.created_at) : '—'}</td>
        <td><button class="btn" data-mcact="detail" data-id="${c.id}">View</button></td>`;
      d.tbody.appendChild(tr);
    });
    if (d.pageInfo) d.pageInfo.textContent = `${Math.floor(customersOffset / customersLimit) + 1}`;
  }

  async function loadCustomers() {
    const seq = ++customersReqSeq;
    try {
      const url = api.routes.adminMobileCustomers(customersQuery, customersLimit, customersOffset);
      const data = await api.fetch(url, { method: 'GET' });
      if (seq !== customersReqSeq) return;
      renderCustomers((data && data.items) || [], data || {});
    } catch (err) {
      if (seq !== customersReqSeq) return;
      renderCustomers([], {});
      notify.showFlash(err.message, 'error');
    }
  }

  function renderPendingPayments(items = [], summary = {}) {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    if (!d.pendingTbody) return;
    d.pendingTbody.innerHTML = '';
    if (!items.length) {
      d.pendingTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No pending payments</td></tr>';
      if (d.pendingPageInfo) d.pendingPageInfo.textContent = `${Math.floor(pendingOffset / pendingLimit) + 1}`;
      return;
    }
    items.forEach((p, idx) => {
      const tr = document.createElement('tr');
      // Pass the row's own currency so a non-default currency renders correctly;
      // formatCurrency already includes the code, so the cell no longer appends it.
      const amount = formatCurrency(p.amount, p.currency);
      tr.innerHTML = `
        <td>${idx + 1 + pendingOffset}</td>
        <td>${p.created_at ? formatDateTimeFull(p.created_at) : '—'}</td>
        <td>
          <div>${escape(p.profile_name || '')}</div>
          <div style="color:var(--sub); font-size:12px;">${escape(p.profile_phone || '')}</div>
        </td>
        <td>
          <div style="font-size:12px;">${escape(p.inet_payment_id || '')}</div>
          <div style="color:var(--sub); font-size:12px;">${escape(p.wave_ref_id || '')}</div>
        </td>
        <td>${amount}</td>
        <td>${escape(p.status || '')}</td>
        <td>
          <button class="btn success" data-mppact="complete" data-id="${p.id}">Complete</button>
          <button class="btn warn" data-mppact="manual" data-id="${p.id}">Mark Manual</button>
          <button class="btn danger" data-mppact="cancel" data-id="${p.id}">Cancel</button>
          <button class="btn ghost" data-mppact="details" data-id="${p.id}">Details</button>
        </td>`;
      d.pendingTbody.appendChild(tr);
      const detail = document.createElement('tr');
      detail.className = 'mpp-detail';
      detail.style.display = 'none';
      detail.innerHTML = `<td colspan="7" style="background:var(--panel-2,#f7f7f9);">${paymentDetailHtml(p)}</td>`;
      d.pendingTbody.appendChild(detail);
    });
    if (d.pendingPageInfo) {
      d.pendingPageInfo.textContent = `${Math.floor(pendingOffset / pendingLimit) + 1} / ${Math.max(1, Math.ceil(Number(summary.total || 0) / pendingLimit))}`;
    }
  }

  async function loadPendingPayments() {
    const seq = ++pendingReqSeq;
    // Recompute the root-only purge button here (not just at mount): mount may
    // run before the current user/roles are known, which would hide it forever.
    const dom0 = resolveDom();
    if (dom0.pendingPurgeStale) {
      dom0.pendingPurgeStale.style.display = hasRole('root') ? '' : 'none';
    }
    try {
      const url = api.routes.adminMobilePendingPayments(pendingQuery, pendingLimit, pendingOffset);
      const data = await api.fetch(url, { method: 'GET' });
      if (seq !== pendingReqSeq) return;
      renderPendingPayments((data && data.items) || [], data || {});
    } catch (err) {
      if (seq !== pendingReqSeq) return;
      renderPendingPayments([], {});
      notify.showFlash(err.message, 'error');
    }
  }

  function setTab(tab) {
    const d = resolveDom();
    activeTab = tab === 'pending_payments' ? 'pending_payments' : 'customers';
    if (d.tabCustomers) d.tabCustomers.style.display = activeTab === 'customers' ? '' : 'none';
    if (d.tabPending) d.tabPending.style.display = activeTab === 'pending_payments' ? '' : 'none';
    if (d.tabBar) {
      Array.from(d.tabBar.querySelectorAll('.tab')).forEach((btn) => {
        const isActive = btn.dataset.mobileCustomersTab === activeTab;
        btn.classList.toggle('active', isActive);
      });
    }
    if (activeTab === 'customers') {
      loadCustomers();
    } else {
      loadPendingPayments();
    }
  }

  async function openCustomerDetail(id) {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    currentProfileId = id;
    if (d.profile) d.profile.innerHTML = '';
    if (d.subs) d.subs.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>';
    if (d.pays) d.pays.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>';
    if (d.keys) d.keys.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>';
    helpers.openDialog(d.dlg);
    const data = await api.fetch(api.routes.adminMobileCustomer(id), { method: 'GET' });
    const p = data.profile || {};
    if (d.profile) {
      d.profile.innerHTML = mobileProfileGridHtml(p);
    }
    if (d.subs) {
      d.subs.innerHTML = '';
      (data.subscriptions || []).forEach((s) => {
        const tr = document.createElement('tr');
        tr.innerHTML = `<td>${escape(s.name || '')}</td><td>${formatCurrency(s.periodic_fee)}</td><td>${escape(String(s.billing_period || ''))}</td><td>${escape(s.charge_model || '')}</td>`;
        d.subs.appendChild(tr);
      });
      if (!(data.subscriptions || []).length) d.subs.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">None</td></tr>';
    }
    mcPaymentsCache = Array.isArray(data.payments) ? data.payments : [];
    renderPayments();
    if (d.keys) {
      d.keys.innerHTML = '';
      (data.auth_keys || []).forEach((k) => {
        const tr = document.createElement('tr');
        tr.innerHTML = `<td>${escape(String(k.id))}</td><td>${k.created_at ? formatDateTimeFull(k.created_at) : ''}</td><td>${k.last_used_at ? formatDateTimeFull(k.last_used_at) : ''}</td><td><button class="btn danger" data-keyid="${k.id}">🗑 Revoke</button></td>`;
        d.keys.appendChild(tr);
      });
      if (!(data.auth_keys || []).length) d.keys.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">None</td></tr>';
    }
  }

  async function refreshBilling() {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    if (!currentProfileId) return;
    if (d.refreshBtn) {
      d.refreshBtn.disabled = true;
      d.refreshBtn.textContent = 'Refreshing…';
    }
    try {
      const res = await api.fetch(`/api/admin/mobile/customers/${currentProfileId}/refresh-billing`, { method: 'POST' });
      const acc = (res && res.account) || {};
      const subs = (res && res.subscriptions) || [];
      // Re-render the whole profile grid: the customer-facing rows (available
      // funds / amount due) are conditional, so patching individual nodes by id
      // would leave them stale. get_account's snapshot carries every field
      // mobileProfileGridHtml needs.
      if (d.profile) d.profile.innerHTML = mobileProfileGridHtml(acc);
      if (d.subs) {
        d.subs.innerHTML = '';
        subs.forEach((s) => {
          const tr = document.createElement('tr');
          tr.innerHTML = `<td>${escape(s.name || '')}</td><td>${formatCurrency(s.periodic_fee)}</td><td>${escape(String(s.billing_period || ''))}</td><td>${escape(s.charge_model || '')}</td>`;
          d.subs.appendChild(tr);
        });
        if (!subs.length) d.subs.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">None</td></tr>';
      }
      if (d.conns && res && Array.isArray(res.connections)) {
        d.conns.innerHTML = '';
        res.connections.forEach((c) => {
          const tr = document.createElement('tr');
          tr.innerHTML = `<td>${escape(c.porta_connection_id || String(c.id || ''))}</td>`
            + `<td>${escape(c.status || '')}</td>`
            + `<td>${escape(c.blocked || '')}</td>`
            + `<td>${escape(c.porta_product_name || '')}</td>`
            + `<td>${c.expiration_date ? formatDateTimeFull(c.expiration_date) : ''}</td>`
            + `<td>${escape(c.porta_bill_status || '')}</td>`
            + `<td>${escape(String(c.idle_days || ''))}</td>`;
          d.conns.appendChild(tr);
        });
        if (!res.connections.length) d.conns.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">None</td></tr>';
      }
      notify.showFlash('Billing refreshed');
    } catch (err) {
      notify.showFlash(err.message, 'error');
    } finally {
      if (d.refreshBtn) {
        d.refreshBtn.disabled = false;
        d.refreshBtn.textContent = '↻ Refresh Billing';
      }
    }
  }

  function mount() {
    if (mounted) return;
    const d = resolveDom();
    if (d.tabBar) {
      d.tabBar.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-mobile-customers-tab]');
        if (!btn) return;
        setTab(btn.dataset.mobileCustomersTab || 'customers');
      });
    }
    if (d.search) {
      d.search.addEventListener('input', debounce(() => {
        customersQuery = d.search.value.trim();
        customersOffset = 0;
        loadCustomers();
      }, 250));
    }
    if (d.prev && d.next) {
      d.prev.addEventListener('click', () => {
        customersOffset = Math.max(0, customersOffset - customersLimit);
        loadCustomers();
      });
      d.next.addEventListener('click', () => {
        customersOffset += customersLimit;
        loadCustomers();
      });
    }
    if (d.pendingSearch) {
      d.pendingSearch.addEventListener('input', debounce(() => {
        pendingQuery = d.pendingSearch.value.trim();
        pendingOffset = 0;
        loadPendingPayments();
      }, 250));
    }
    if (d.pendingPrev && d.pendingNext) {
      d.pendingPrev.addEventListener('click', () => {
        pendingOffset = Math.max(0, pendingOffset - pendingLimit);
        loadPendingPayments();
      });
      d.pendingNext.addEventListener('click', () => {
        pendingOffset += pendingLimit;
        loadPendingPayments();
      });
    }
    if (d.pendingRefresh) {
      d.pendingRefresh.addEventListener('click', () => loadPendingPayments());
    }
    if (d.pendingPurgeStale) {
      // Root-only destructive bulk action.
      d.pendingPurgeStale.style.display = hasRole('root') ? '' : 'none';
      d.pendingPurgeStale.addEventListener('click', async () => {
        if (!hasRole('root')) return;
        if (!confirm('Permanently delete all payments stuck in "initiated" status for more than 30 days? This cannot be undone.')) return;
        d.pendingPurgeStale.disabled = true;
        try {
          const res = await api.fetch(api.routes.adminMobilePurgeStaleInitiated(), { method: 'POST' });
          notify.showFlash(`Deleted ${(res && res.deleted) || 0} stale payment(s)`);
          await loadPendingPayments();
        } catch (err) {
          notify.showFlash(err.message || 'Failed to purge stale payments', 'error');
        } finally {
          d.pendingPurgeStale.disabled = false;
        }
      });
    }
    if (d.tbody) {
      d.tbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-mcact]');
        if (!btn) return;
        const id = btn.dataset.id;
        try { await openCustomerDetail(id); }
        catch (err) { notify.showFlash(err.message, 'error'); }
      });
    }
    if (d.pendingTbody) {
      d.pendingTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-mppact]');
        if (!btn) return;
        const paymentId = btn.dataset.id;
        const action = btn.dataset.mppact;
        if (!paymentId || !action) return;
        if (action === 'details') {
          const detail = btn.closest('tr') && btn.closest('tr').nextElementSibling;
          if (detail && detail.classList.contains('mpp-detail')) {
            detail.style.display = detail.style.display === 'none' ? '' : 'none';
          }
          return;
        }
        btn.disabled = true;
        try {
          if (action === 'complete') {
            const result = await api.fetch(api.routes.adminMobilePendingPaymentComplete(paymentId), { method: 'POST' });
            if (result && result.ok) {
              notify.showFlash('Payment completed and posted to Porta.');
            } else if (result && result.terminal === false) {
              notify.showFlash(`Wave status is still ${result.wave_status || 'processing'}.`, 'info');
            } else if (result && result.terminal === true) {
              notify.showFlash(`Payment marked failed by Wave status: ${result.wave_status || 'failed'}.`, 'error');
            } else {
              notify.showFlash('Payment not yet finalized.', 'info');
            }
          } else if (action === 'manual') {
            if (!confirm('Mark this payment as manually processed in admin?')) return;
            await api.fetch(api.routes.adminMobilePendingPaymentManualProcessed(paymentId), { method: 'POST' });
            notify.showFlash('Payment marked as manually processed.');
          } else if (action === 'cancel') {
            const ok = await cancelPaymentFlow(paymentId);
            if (!ok) return;
          }
          await loadPendingPayments();
        } catch (err) {
          notify.showFlash(err.message || 'Action failed', 'error');
        } finally {
          btn.disabled = false;
        }
      });
    }
    if (d.refreshBtn) {
      d.refreshBtn.addEventListener('click', refreshBilling);
    }
    if (d.payStatusFilter) {
      d.payStatusFilter.addEventListener('change', () => renderPayments());
    }
    if (d.manualPayForm) {
      d.manualPayForm.addEventListener('submit', submitManualPay);
    }
    if (d.keys) {
      d.keys.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-keyid]');
        if (!btn) return;
        const keyId = btn.dataset.keyid;
        if (!confirm('Revoke this mobile session?')) return;
        try {
          await api.fetch(api.routes.adminMobileRevoke(keyId), { method: 'POST' });
          notify.showFlash('Mobile session revoked');
          btn.closest('tr').remove();
        } catch (err) {
          notify.showFlash(err.message, 'error');
        }
      });
    }
    mounted = true;
  }

  const PENDING_PAY_STATES = new Set(['initiated', 'processing']);

  // Full transaction detail (shown in the expandable row under a payment).
  function paymentDetailHtml(p) {
    const esc = helpers.escapeHtml;
    const dt = (v) => (v ? formatDateTimeFull(v) : '');
    const line = (k, v) => (v == null || v === '' ? ''
      : `<div style="display:flex; gap:8px;"><span style="min-width:150px; color:var(--sub);">${k}</span><span>${esc(String(v))}</span></div>`);
    return `<div style="padding:8px 10px; display:flex; flex-direction:column; gap:3px; font-size:12px;">
      ${line('INET payment id', p.inet_payment_id)}
      ${line('Wave ref', p.wave_ref_id)}
      ${line('Porta tx id', p.porta_tx_id)}
      ${line('Status', p.status)}
      ${line('Amount', p.amount != null ? formatCurrency(p.amount, p.currency) : '')}
      ${line('Created', dt(p.created_at))}
      ${line('Updated', dt(p.updated_at))}
      ${line('Completed by', p.manual_completed_by)}
      ${line('Completed at', dt(p.manual_completed_at))}
      ${line('Payer name', p.manual_payer_name)}
      ${line('Cancelled by', p.cancelled_by)}
      ${line('Cancel reason', p.cancel_reason)}
      ${line('Cancelled at', dt(p.cancelled_at))}
      ${line('Failure reason', p.failure_reason)}
    </div>`;
  }

  // Prompt for a reason and cancel an incomplete transaction. Returns the updated
  // payment object on success, or false if the user aborted / it failed.
  async function cancelPaymentFlow(paymentId) {
    const reason = window.prompt('Reason for cancelling this transaction (required):', '');
    if (reason == null) return false;                 // dismissed
    if (!reason.trim()) { notify.showFlash('A reason is required to cancel.', 'error'); return false; }
    try {
      const res = await api.fetch(api.routes.adminMobilePaymentCancel(paymentId), {
        method: 'POST', body: JSON.stringify({ reason: reason.trim() }),
      });
      notify.showFlash('Transaction cancelled.');
      return (res && res.payment) || true;
    } catch (err) {
      notify.showFlash(err.message || 'Cancel failed', 'error');
      return false;
    }
  }

  // Render the Recent Payments table from the cached payments, applying the
  // status filter, showing the created date, and per-row actions (manually
  // complete an initiated payment / view a stored proof).
  function renderPayments() {
    const d = resolveDom();
    if (!d.pays) return;
    const escape = helpers.escapeHtml;
    const filter = (d.payStatusFilter && d.payStatusFilter.value) || '';
    const rows = (mcPaymentsCache || []).filter((r) => !filter || String(r.status || '').toLowerCase() === filter);
    d.pays.innerHTML = '';
    if (!rows.length) {
      d.pays.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">${filter ? 'No payments with this status' : 'None'}</td></tr>`;
      return;
    }
    rows.forEach((r) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `<td>${escape(r.wave_ref_id || r.inet_payment_id || '')}</td>`
        + `<td>${formatCurrency(r.amount)}</td>`
        + `<td>${escape(r.status || '')}</td>`
        + `<td>${r.created_at ? formatDateTimeFull(r.created_at) : ''}</td>`;
      const actions = document.createElement('td');
      const isPending = PENDING_PAY_STATES.has(String(r.status || '').toLowerCase());
      if (isPending) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn small';
        btn.textContent = 'Complete';
        btn.title = 'Manually complete with Wave proof';
        btn.addEventListener('click', () => openManualPayDialog(r));
        actions.appendChild(btn);
      }
      if (r.has_proof) {
        const viewBtn = document.createElement('button');
        viewBtn.type = 'button';
        viewBtn.className = 'btn small ghost';
        viewBtn.style.marginLeft = actions.childNodes.length ? '6px' : '0';
        viewBtn.textContent = 'View proof';
        viewBtn.title = r.manual_payer_name ? `Paid by ${r.manual_payer_name}` : 'View stored proof';
        viewBtn.addEventListener('click', () => window.open(api.routes.adminMobilePaymentProof(r.id), '_blank', 'noopener'));
        actions.appendChild(viewBtn);
      }
      // The expandable detail row (who/why for a cancellation, all fields).
      const detailTr = document.createElement('tr');
      detailTr.style.display = 'none';
      detailTr.innerHTML = `<td colspan="5" style="background:var(--panel-2,#f7f7f9);">${paymentDetailHtml(r)}</td>`;

      if (isPending) {
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'btn small danger';
        cancelBtn.style.marginLeft = actions.childNodes.length ? '6px' : '0';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.title = 'Cancel this incomplete transaction';
        cancelBtn.addEventListener('click', async () => {
          const res = await cancelPaymentFlow(r.id);
          if (!res) return;
          const idx = mcPaymentsCache.findIndex((x) => x.id === r.id);
          if (idx >= 0 && typeof res === 'object') mcPaymentsCache[idx] = res;
          renderPayments();
        });
        actions.appendChild(cancelBtn);
      }
      const detailsBtn = document.createElement('button');
      detailsBtn.type = 'button';
      detailsBtn.className = 'btn small ghost';
      detailsBtn.style.marginLeft = actions.childNodes.length ? '6px' : '0';
      detailsBtn.textContent = 'Details';
      detailsBtn.addEventListener('click', () => {
        detailTr.style.display = detailTr.style.display === 'none' ? '' : 'none';
      });
      actions.appendChild(detailsBtn);

      if (!actions.childNodes.length) actions.textContent = '—';
      tr.appendChild(actions);
      d.pays.appendChild(tr);
      d.pays.appendChild(detailTr);
    });
  }

  function openManualPayDialog(payment) {
    const d = resolveDom();
    if (!d.manualPayDlg) return;
    mcManualPayment = payment;
    if (d.manualPayForm) d.manualPayForm.reset();
    if (d.manualPaySummary) {
      const ref = payment.wave_ref_id || payment.inet_payment_id || '';
      d.manualPaySummary.textContent = `${ref} · ${formatCurrency(payment.amount)} · ${payment.status || ''}`;
    }
    helpers.openDialog(d.manualPayDlg);
    if (d.manualPayerName) d.manualPayerName.focus();
  }

  async function submitManualPay(event) {
    event.preventDefault();
    const d = resolveDom();
    if (!mcManualPayment) return;
    const payer = (d.manualPayerName && d.manualPayerName.value.trim()) || '';
    const file = d.manualProof && d.manualProof.files && d.manualProof.files[0];
    if (!payer) { notify.showFlash('Enter the payer name.', 'error'); return; }
    if (!file) { notify.showFlash('Attach the Wave proof image.', 'error'); return; }
    const fd = new FormData();
    fd.append('payer_name', payer);
    fd.append('proof', file);
    if (d.manualPaySubmit) { d.manualPaySubmit.disabled = true; d.manualPaySubmit.textContent = 'Completing…'; }
    try {
      await api.fetch(api.routes.adminMobilePaymentManualComplete(mcManualPayment.id), { method: 'POST', body: fd });
      notify.showFlash('Payment completed and credited to PortaOne');
      try { d.manualPayDlg.close(); } catch { if (d.manualPayDlg) d.manualPayDlg.open = false; }
      const id = currentProfileId;
      if (id) await openCustomerDetail(id);
    } catch (err) {
      notify.showFlash(err.message || 'Failed to complete payment', 'error');
    } finally {
      if (d.manualPaySubmit) { d.manualPaySubmit.disabled = false; d.manualPaySubmit.textContent = 'Complete & credit'; }
    }
  }

export default {
  mount,
  refresh: loadCustomers,
  refreshPending: loadPendingPayments,
  setTab,
  openDetail: openCustomerDetail,
};
